import { getPriceString, getIdByGraphQlId } from 'libs/shopify'

/**
 * Normalize custom fields.
 *
 * @param {Array<object>} customFields - Array of custom fields.
 * @return {object} - Normalized custom fields.
 */
const resultCustomFields = (customFields) => customFields.filter(Boolean).reduce((item, { namespace, key, value, reference }) => {
  const scent = reference?.type === 'scent' && reference.fields.map(field => field.value).join(',');
  const awardImage = reference?.image?.url ;

  if (scent) {
     return { ...item, [`${namespace}_${key}`]: scent }
  }

  if (awardImage) {
    return { ...item, [`${namespace}_${key}`]: awardImage }
  }

  return { ...item, [`${namespace}_${key}`]: value }
}, {})

/**
 * Get variant price.
 *
 * @param {number} amount - Price amount.
 * @return {number|undefined} - Normalized price.
 */
const getVariantPrice = (amount) => amount !== undefined ? getPriceString(amount) : undefined

/**
 * Get image details.
 *
 * @param {object} item - Image item.
 * @return {object|null} - Normalized image details.
 */
const getImage = (item, fallbackAlt = '') => item?.id ? {
  id: getIdByGraphQlId(item.id),
  src: item.src,
  alt: item.altText || fallbackAlt,
  width: item.width,
  height: item.height
} : null

/**
 * Get multiple images.
 *
 * @param {Array<object>} items - Array of image items.
 * @return {Array<object>} - Array of normalized images.
 */
const getImages = (items, fallbackAlt) => items?.length ? items.map((item) => getImage(item, fallbackAlt)) : []

/**
 * Get variant details.
 *
 * @param {object} variant - Variant item.
 * @return {object} - Normalized variant details.
 */
const getVariant = (variant) => ({
  available: variant.availableForSale,
  barcode: variant.barcode,
  id: getIdByGraphQlId(variant.id),
  sku: variant.sku,
  title: variant.title,
  price: getVariantPrice(variant.price?.amount),
  compare_at_price: getVariantPrice(variant.compareAtPrice?.amount),
  image: getImage(variant.image, variant.title),
  inventory_quantity: variant.quantityAvailable,
  options: variant.selectedOptions.map(({ value }) => value)
})

/**
 * Get multiple variants.
 *
 * @param {Array<object>} variants - Array of variant items.
 * @return {Array<object>} - Array of normalized variants.
 */
const getVariants = (variants) => variants?.map(getVariant) || []

/**
 * Normalize product data from GraphQL.
 *
 * @param {object} product - Product item.
 * @return {object} - Normalized product details.
 */
const normalizeProductGraphql = (product) => ({
  id: getIdByGraphQlId(product.id),
  available: product.availableForSale,
  vendor: product.vendor,
  type: product.productType,
  title: product.title,
  handle: product.handle,
  url: product.onlineStoreUrl,
  isGiftCard: product.isGiftCard,
  featuredImage: getImage(product.featuredImage, product.title),
  images: getImages(product.images.nodes, product.title),
  price: getPriceString(product.priceRange.minVariantPrice.amount),
  compare_at_price: getPriceString(product.compareAtPriceRange.minVariantPrice.amount),
  tags: product.tags,
  initialVariant: getVariant(product.variants.nodes[0]),
  variants: getVariants(product.variants.nodes),
  metafields: resultCustomFields(product.metafields)
})

const getBundlesChild = (variants) => {
  if (!variants) return []

  const arrayProductBundle = variants.flatMap((variant) => variant.components.nodes)

  if (!arrayProductBundle.length) return []

  return arrayProductBundle.map((item) => ({
    variant_id: getIdByGraphQlId(item.productVariant.id),
    id: getIdByGraphQlId(item.productVariant.product.id)
  }))
}

const normalizeProductBundleGraphql = (product) => ({
  id: getIdByGraphQlId(product.id),
  title: product.title,
  handle: product.handle,
  bundlesChild: getBundlesChild(product.variants.nodes)
})

/**
 * Normalize products based on type.
 *
 * @param {Array<object>} products - Array of products.
 * @param {string} type - Type of normalization.
 * @return {Array<object>} - Array of normalized products.
 */
const normalizeProducts = (products, type) => {
  if (!products?.length) {
    console.warn('normalizeProducts was called without products')

    return []
  }

  switch (type) {
    case 'shopify-graphql':
    case 'shopify-graphql-recommendations':
      return products.map(normalizeProductGraphql)
    case 'shopify-graphql-bundles':
      return products.map(normalizeProductBundleGraphql)
    default:
      return []
  }
}

export default normalizeProducts
